import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setSelectedContact,
  clearSelectedContact,
} from "../../../../../../redux/mgmtCo/selectedContactSlice";
import {
  setSelectedCustomer,
  clearSelectedCustomer,
} from "../../../../../../redux/mgmtCo/selectedCustomerSlice";
import {
  addAssignedContact,
  updateAssignedContact,
} from "../../../../../../redux/mgmtCo/contactsSlice";
import {
  addCustomerToState,
  updateCustomerInState,
} from "../../../../../../redux/mgmtCo/managementCustomerSlice";

// Import the universal component.
import GenericInfoPanel from "../../../../../../components/Modals/GenericInfoPanel";

// Import your configuration objects.
import {
  contactFields,
  contactAdditionalFields,
  initialContactFormData,
} from "../../config/mgmtContactConfig";
import {
  customerInfoWithoutMgmt as customerGeneralInfo,
  customerComment,
  customerAddressInfo,
  customerBillingInfo,
  initialCustomerFormData,
} from "../../../Customer/customerFormDataConfig";

// Import API service functions.
import { updateContact, addNewContact } from "../../services/managementService";
import {
  updateCustomer,
  createNewCustomer,
} from "../../services/managementCustomerService";

const InformationDisplay = ({
  isEditMode,
  newMode, // true if a new entity is being created
  newEntityType, // "contact" or "customer" (only used when newMode is true)
  onCloseNewMode, // Callback to exit new mode (e.g. on cancel or after submit)
  updateList, // Callback to refresh the list after submit
}) => {
  const dispatch = useDispatch();
  const selectedContact = useSelector(
    (state) => state.selectedManagementContact
  );
  const selectedCustomer = useSelector((state) => state.selectedCustomer);
  const [formData, setFormData] = useState(null);

  // Initialize or update formData based on whether we're in newMode or editing an existing entity.
  useEffect(() => {
    if (newMode) {
      if (newEntityType === "contact") {
        setFormData(initialContactFormData);
        dispatch(clearSelectedContact());
      } else if (newEntityType === "customer") {
        setFormData(initialCustomerFormData);
        dispatch(clearSelectedCustomer());
      }
    } else {
      if (selectedContact && selectedContact.contactName !== undefined) {
        setFormData(selectedContact);
      } else if (
        selectedCustomer &&
        selectedCustomer.customerName !== undefined
      ) {
        setFormData(selectedCustomer);
      } else {
        setFormData(null);
      }
    }
  }, [newMode, newEntityType, dispatch, selectedContact, selectedCustomer]);

  // Single change handler for new and existing entities.
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // For existing entities, update Redux state as well.
    if (!newMode) {
      if (selectedContact && selectedContact.contactName !== undefined) {
        const updated = { ...selectedContact, [name]: value };
        dispatch(setSelectedContact(updated));
      } else if (
        selectedCustomer &&
        selectedCustomer.customerName !== undefined
      ) {
        const updated = { ...selectedCustomer, [name]: value };
        dispatch(setSelectedCustomer(updated));
      }
    }
  };

  // Determine the entity type based on newMode and selected data.
  let entityType = null;
  if (newMode) {
    entityType = newEntityType;
  } else if (selectedContact && selectedContact.contactName !== undefined) {
    entityType = "contact";
  } else if (selectedCustomer && selectedCustomer.customerName !== undefined) {
    entityType = "customer";
  }

  if (!formData || !entityType) {
    return (
      <div className="text-center text-gray-500">
        <p>No contact or customer selected.</p>
      </div>
    );
  }

  // Configure the title and field sections based on the entity type.
  let title = "";
  let fieldSections = [];
  if (entityType === "contact") {
    title = newMode ? "New Contact" : `Contact - ${formData.contactName}`;
    fieldSections = [
      { label: "General", fields: contactFields },
      { label: "Additional Info", fields: contactAdditionalFields },
    ];
  } else if (entityType === "customer") {
    title = newMode ? "New Customer" : `Customer - ${formData.customerName}`;
    fieldSections = [
      { label: "General", fields: customerGeneralInfo },
      { label: "Address", fields: customerAddressInfo },
      { label: "Billing", fields: customerBillingInfo },
    ];
  }

  // Handle submission for new or existing entities.
  const handleSubmit = async () => {
    try {
      let updatedEntity;
      if (newMode) {
        if (entityType === "contact") {
          updatedEntity = await addNewContact(formData);
          dispatch(addAssignedContact(updatedEntity));
        } else if (entityType === "customer") {
          updatedEntity = await createNewCustomer(formData);
          dispatch(addCustomerToState(updatedEntity));
        }
      } else {
        if (entityType === "contact") {
          updatedEntity = await updateContact(formData.contactId, formData);
          dispatch(
            updateAssignedContact({
              contactID: formData.contactId,
              updates: updatedEntity,
            })
          );
          dispatch(setSelectedContact(updatedEntity));
        } else if (entityType === "customer") {
          updatedEntity = await updateCustomer(formData.customerID, formData);
          dispatch(updateCustomerInState(updatedEntity));
          dispatch(setSelectedCustomer(updatedEntity));
        }
      }
      updateList();
      onCloseNewMode();
    } catch (error) {
      console.error("Submit failed:", error);
      alert(`Submit failed: ${error.message}`);
    }
  };

  return (
    <GenericInfoPanel
      title={title}
      fieldSections={fieldSections}
      formData={formData}
      isEditMode={isEditMode}
      newMode={newMode}
      onChange={handleChange}
      onSubmit={handleSubmit}
      onCancel={onCloseNewMode}
    />
  );
};

export default InformationDisplay;
